/**
 * decisionHistoryService — authFetch wrappers around
 * /api/admin/ai/decisions/* (extended in WQ-235).
 *
 * Consumes:
 *   GET  /api/admin/ai/decisions?... (extended filters: gate_names CSV,
 *        start_time, end_time)
 *   GET  /api/admin/ai/decisions/count?... (total companion)
 *   GET  /api/admin/ai/decisions/gate-names (distinct gate names)
 *
 * All endpoints require AI_ADMIN permission (operator-only).
 * IL-3 audit-bearing read surface.
 */

export interface GateDecision {
  id: string;
  gate_name: string;
  lifecycle_id: string | null;
  policy_id: string | null;
  inputs_hash: string | null;
  decision: string; // approved | denied | escalated | pending
  rationale_text: string | null;
  actor_type: string;
  actor_id: string | null;
  tenant_id: string | null;
  decided_at: string | null;
  decision_metadata: Record<string, unknown>;
}

export interface DecisionHistoryFilters {
  tenant_id?: string;
  capability_type?: string; // surfaced via decision_metadata; client-side filter
  gate_names?: string[]; // multi-select; serialized as CSV
  decision?: string; // approved/denied/escalated/pending
  start_time?: string; // ISO 8601
  end_time?: string; // ISO 8601
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/ai/decisions';

function buildQuery(filters: DecisionHistoryFilters, limit?: number, offset?: number): string {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters.gate_names && filters.gate_names.length > 0) {
    params.set('gate_names', filters.gate_names.join(','));
  }
  if (filters.decision) params.set('decision', filters.decision);
  if (filters.start_time) params.set('start_time', filters.start_time);
  if (filters.end_time) params.set('end_time', filters.end_time);
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  return params.toString();
}

export async function listDecisions(
  authFetch: AuthFetch,
  filters: DecisionHistoryFilters = {},
  limit = 50,
  offset = 0,
): Promise<GateDecision[]> {
  const qs = buildQuery(filters, limit, offset);
  const url = qs ? `${BASE}?${qs}` : BASE;
  const res = await authFetch(url);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`listDecisions failed: ${res.status} ${detail}`);
  }
  let rows: GateDecision[] = await res.json();
  // capability_type is not a server-side filter on platform_gate_decisions —
  // it lives in decision_metadata. Apply client-side after the network call.
  if (filters.capability_type) {
    rows = rows.filter(
      (r) => (r.decision_metadata?.capability_type as string | undefined) === filters.capability_type,
    );
  }
  return rows;
}

export async function countDecisions(
  authFetch: AuthFetch,
  filters: DecisionHistoryFilters = {},
): Promise<number> {
  const qs = buildQuery(filters);
  const url = qs ? `${BASE}/count?${qs}` : `${BASE}/count`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`countDecisions failed: ${res.status}`);
  const body = await res.json();
  return Number(body.total ?? 0);
}

export async function listGateNames(authFetch: AuthFetch): Promise<string[]> {
  const res = await authFetch(`${BASE}/gate-names`);
  if (!res.ok) throw new Error(`listGateNames failed: ${res.status}`);
  return res.json();
}

export type TimeRangePreset = '1h' | '24h' | '7d' | '30d' | 'custom';

export function computeTimeRange(preset: TimeRangePreset): { start_time?: string; end_time?: string } {
  if (preset === 'custom') return {};
  const now = new Date();
  const ms: Record<Exclude<TimeRangePreset, 'custom'>, number> = {
    '1h': 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };
  const start = new Date(now.getTime() - ms[preset]);
  return { start_time: start.toISOString(), end_time: now.toISOString() };
}
